import { memo } from "react";
import { AlertCircle, Check, X } from "lucide-react";

import { cn } from "@/lib/utils";
import type { AttachedFile } from "./types";
import { AttachedFileThumbnail } from "./AttachedFileThumbnail";

const isImage = (f: AttachedFile) => f.file.type.startsWith("image/");

export const FileChips = memo(function FileChips({
  files,
  onRemove,
}: {
  files: AttachedFile[];
  onRemove: (id: string) => void;
}) {
  if (files.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 px-3 pb-2">
      {files.map((f) => (
        <div
          key={f.id}
          className={cn(
            "relative overflow-hidden rounded-lg border text-[12px]",
            f.status === "uploading" && "border-blue-500/20 text-blue-400",
            f.status === "done" &&
              "border-emerald-500/20 bg-emerald-500/[0.05] text-emerald-400",
            f.status === "error" &&
              "border-red-500/20 bg-red-500/[0.05] text-red-400",
          )}
        >
          {f.status === "uploading" && (
            <div
              className="absolute inset-0 bg-blue-500/[0.08] transition-[width] duration-200 ease-out"
              style={{ width: `${f.progress}%` }}
            />
          )}
          <div className="relative flex items-center gap-1.5 px-2 py-1">
            {isImage(f) && <AttachedFileThumbnail file={f} size={16} />}
            {f.status === "uploading" && (
              <span className="min-w-[28px] text-[11px] tabular-nums">
                {f.progress}%
              </span>
            )}
            {f.status === "done" && <Check className="h-3 w-3" />}
            {f.status === "error" && <AlertCircle className="h-3 w-3" />}
            <span className="max-w-[150px] truncate">{f.file.name}</span>
            <button
              type="button"
              onClick={() => onRemove(f.id)}
              className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-white/[0.1]"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
});
